// Helpers for antd `Tree checkable` filter lists (Activity Center opportunity
// type / status / source tabs).

const indexTree = treeData => {
  const parentOf = {};
  const childrenOf = {};
  const walk = (nodes, parentKey = null) =>
    (nodes || []).forEach(node => {
      const key = String(node.key);
      parentOf[key] = parentKey;
      childrenOf[key] = (node.children || []).map(child => String(child.key));
      walk(node.children, key);
    });
  walk(treeData);
  return { parentOf, childrenOf };
};

/**
 * Toggle one key with antd's conduction rules (checking a node checks its
 * descendants and any ancestor whose children are now all checked; unchecking
 * clears its descendants and ancestors). Runs on the FULL tree, so selections
 * hidden by an active search are kept — antd's own `onCheck` keys drop them.
 * Keys not in the tree are preserved. Returns string keys.
 */
export const toggleTreeKey = (treeData, checkedKeys, key) => {
  const { parentOf, childrenOf } = indexTree(treeData);
  const target = String(key);
  const checked = new Set((checkedKeys || []).map(String));

  const subtree = [];
  const collect = k => {
    subtree.push(k);
    (childrenOf[k] || []).forEach(collect);
  };
  collect(target);

  if (checked.has(target)) {
    subtree.forEach(k => checked.delete(k));
    for (let p = parentOf[target]; p; p = parentOf[p]) checked.delete(p);
  } else {
    subtree.forEach(k => checked.add(k));
    for (let p = parentOf[target]; p; p = parentOf[p]) {
      if (!childrenOf[p].every(c => checked.has(c))) break;
      checked.add(p);
    }
  }
  return [...checked];
};

/**
 * Merge sibling nodes that share a title (case-insensitive) into one node,
 * recursively. Each opportunity type defines its own statuses, so a location
 * with several types returns e.g. three "Unqualified" rows with different ids.
 * The merged node is keyed by its lowest member id; `idsByKey` maps that key
 * back to every member id and `keyById` maps any member id to the merged key.
 */
export const mergeSameTitleSiblings = treeData => {
  const idsByKey = {};
  const keyById = {};

  const merge = nodes => {
    const groups = new Map();
    (nodes || []).forEach(node => {
      const name = String(node.title ?? "")
        .trim()
        .toLowerCase();
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push(node);
    });

    return [...groups.values()]
      .map(group => {
        const ids = group.map(node => String(node.key)).sort((a, b) => Number(a) - Number(b));
        const key = ids[0];
        idsByKey[key] = ids;
        ids.forEach(id => {
          keyById[id] = key;
        });
        const children = merge(group.flatMap(node => node.children || []));
        return {
          ...group[0],
          key,
          ...(children.length > 0 ? { children } : { children: undefined }),
        };
      })
      .sort((a, b) =>
        String(a.title ?? "")
          .toLowerCase()
          .localeCompare(String(b.title ?? "").toLowerCase())
      );
  };

  return { treeData: merge(treeData), idsByKey, keyById };
};
